import { apiHooks, setQueriesData } from '../../../hooks/useRestAPIHooks';
import { useReportErrorToBugsnag } from '../../../util/bugsnagReporting';
import { CompositeReportDefinitionDetail, CreateItem } from '../models';
import { useNotify } from '../../../hooks';
import { defineMessages, useIntl } from 'react-intl';
import { useInvalidateTemplatingItemQueries } from './useManageTemplatingItemQueries';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SELECTED_TEMPLATING_ITEM_QUERY_KEY } from './useSelectedTemplatingItem';
import { CompositeReportDefinitionDetailRawData } from '../types';

const messages = defineMessages({
  compositeReportDefinitionSaved: {
    id: 'compositeReportDefinitionSaved',
    defaultMessage: 'Composite report template saved',
  },
  compositeReportDefinitionSaveFailed: {
    id: 'compositeReportDefinitionSaveFailed',
    defaultMessage: 'Failed to save composite report template',
  },
});

export const useSaveCompositeReportDefinition = () => {
  const { formatMessage } = useIntl();
  const invalidateCompositeReportDefinition =
    useInvalidateTemplatingItemQueries();
  const optimisticUpdate = useOptimisticUpdate();
  const [notify] = useNotify();
  const reportError = useReportErrorToBugsnag();

  const { mutateAsync: saveCompositeReportDefinition } =
    apiHooks.useAPIMutation('PATCH /v1/ai-templates/:id');

  const save = useCallback(
    async (
      compositeReportDefinition: CompositeReportDefinitionDetail,
      update: Partial<
        Omit<CreateItem<CompositeReportDefinitionDetail>, 'path'>
      >,
      { showNotification = true }: { showNotification?: boolean } = {},
    ) => {
      try {
        const updatedCompositeReportDefinition = {
          ...compositeReportDefinition,
          ...update,
        };
        optimisticUpdate(updatedCompositeReportDefinition);
        await saveCompositeReportDefinition(updatedCompositeReportDefinition);
        if (showNotification) {
          notify(
            formatMessage(messages.compositeReportDefinitionSaved),
            'success',
          );
        }
      } catch (error) {
        reportError(error);
        if (showNotification) {
          notify(
            formatMessage(messages.compositeReportDefinitionSaveFailed),
            'error',
          );
        }
      } finally {
        // Bugbot: the optimistic update above can leave the selected CRD
        // query showing sections that were never actually persisted if the
        // PATCH fails (or if optimisticUpdate itself throws). Always
        // invalidate so the cache is refetched from the server as the
        // source of truth, on both success and failure.
        await invalidateCompositeReportDefinition(compositeReportDefinition);
      }
    },
    [
      optimisticUpdate,
      saveCompositeReportDefinition,
      invalidateCompositeReportDefinition,
      notify,
      formatMessage,
      reportError,
    ],
  );

  return {
    save,
  };
};

function useOptimisticUpdate() {
  const queryClient = useQueryClient();

  const optimisticUpdate = useCallback(
    (crd: CompositeReportDefinitionDetail) => {
      setQueriesData(queryClient, {
        route: SELECTED_TEMPLATING_ITEM_QUERY_KEY,
        // `SELECTED_TEMPLATING_ITEM_QUERY_KEY` ("GET /v1/ai-templates/durable/:durableId")
        // is queried with `{ durableId }` params (see useSelectedTemplatingItem.tsx),
        // not `{ id }` — comparing on `payload.id` here always fails, which
        // silently no-ops this optimistic update for every CRD.
        predicate: (payload: { durableId: string }) =>
          payload.durableId === crd.durableId,
        dataTransformer: (
          oldData:
            | { template: CompositeReportDefinitionDetailRawData }
            | undefined,
        ) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            template: {
              ...oldData.template,
              sections: crd.sections,
            },
          };
        },
      });
    },
    [queryClient],
  );

  return optimisticUpdate;
}
